import * as React from 'react';

export type AILogoVariant = 'dark' | 'light' | 'yellowfin' | 'monochrome';

const PATH_DATA = [
  'M32.2871 15.1692C26.8054 10.5534 22.0839 5.46043 18.1727 0C15.205 1.4739 12.4494 3.30417 9.964 5.43479C14.4641 11.8179 19.8322 17.6418 25.8951 22.7243C31.7253 27.5902 40.485 27.5703 46.2998 22.6915C52.4297 17.5217 57.7468 11.7747 62.1676 5.57625C59.6755 3.41629 56.9081 1.55954 53.9242 0.0641588C50.0224 5.50027 45.32 10.5716 39.8685 15.1692C37.6822 16.994 34.4734 16.994 32.2871 15.1692Z',
  'M56.6639 31.8406C61.3305 26.4186 66.4795 21.7485 72 17.8798C70.5099 14.9444 68.6595 12.2188 66.5054 9.76043C60.0522 14.2115 54.1641 19.5213 49.0257 25.5182C44.1063 31.285 44.1264 39.9493 49.0589 45.7009C54.2856 51.7641 60.0958 57.0233 66.3624 61.3961C68.5461 58.9311 70.4233 56.1939 71.9351 53.2424C66.4392 49.3831 61.3122 44.7318 56.6639 39.3396C54.8191 37.177 54.8191 34.0032 56.6639 31.8406Z',
  'M40.0024 56.8308C45.4841 61.4466 50.2056 66.5396 54.1168 72C57.0845 70.5261 59.8401 68.6958 62.3255 66.5652C57.8254 60.1821 52.4573 54.3582 46.3944 49.2757C40.5642 44.4098 31.8045 44.4297 25.9897 49.3085C19.8598 54.4783 14.5427 60.2253 10.1219 66.4237C12.614 68.5837 15.3813 70.4405 18.3653 71.9358C22.2671 66.4997 26.9695 61.4284 32.421 56.8308C34.6073 55.006 37.8161 55.006 40.0024 56.8308Z',
  'M15.3361 39.3159C10.6695 44.7379 5.52049 49.408 0 53.2767C1.49011 56.2122 3.34051 58.9378 5.49457 61.3961C11.9478 56.945 17.8359 51.6352 22.9743 45.6383C27.8937 39.8716 27.8736 31.2072 22.9411 25.4556C17.7144 19.3924 11.9042 14.1332 5.63758 9.76043C3.45386 12.2255 1.57669 14.9627 0.0648629 17.9141C5.56077 21.7735 10.6878 26.4247 15.3361 31.8169C17.1809 33.9795 17.1809 37.1533 15.3361 39.3159Z',
] as const;

const VARIANT_FILLS: Record<Exclude<AILogoVariant, 'yellowfin' | 'monochrome'>, string> = {
  dark: '#262626',
  light: '#FAFAFA',
};

type AILogoPathsProps = {
  variant: AILogoVariant;
  gradientId: string;
};

export function AILogoPaths({ variant, gradientId }: AILogoPathsProps) {
  if (variant === 'yellowfin') {
    return (
      <>
        <defs>
          <linearGradient id={gradientId} x1="5.37931" y1="0" x2="50.046" y2="65.8568" gradientUnits="userSpaceOnUse">
            <stop offset="0.0544031" stopColor="#1868DB" />
            <stop offset="0.5" stopColor="#357DE8" />
            <stop offset="0.802885" stopColor="#7EE2B8" />
            <stop offset="1" stopColor="#B4EE2B" />
          </linearGradient>
        </defs>
        {PATH_DATA.map((d, i) => (
          <path key={i} d={d} fill={`url(#${gradientId})`} />
        ))}
      </>
    );
  }

  if (variant === 'monochrome') {
    return (
      <>
        {PATH_DATA.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </>
    );
  }

  const fill = VARIANT_FILLS[variant];

  return (
    <>
      {PATH_DATA.map((d, i) => (
        <path key={i} d={d} fill={fill} />
      ))}
    </>
  );
}

export interface AILogoProps extends React.SVGProps<SVGSVGElement> {
  variant: AILogoVariant;
}

export const AILogo = React.forwardRef<SVGSVGElement, AILogoProps>(
  ({ variant, width = 72, height = 72, ...props }, ref) => {
    const id = React.useId();
    const gradientId = `ai-logo-gradient-${id}`;

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        {...props}
        width={width}
        height={height}
        ref={ref}
        viewBox="0 0 72 72"
        role="img"
        fill="none"
      >
        <title>BrainfishAI logo</title>
        <AILogoPaths variant={variant} gradientId={gradientId} />
      </svg>
    );
  },
);

AILogo.displayName = 'AILogo';
